import { parseExpressionThunk, type ValueResolver } from '@examdojo/core/widget';

/**
 * Compile a math expression string into a callable function of one variable.
 *
 * Uses the shared expression parser which supports: arithmetic (+, -, *, /),
 * math functions (sin, cos, tan, sqrt, abs, pow, log, exp, etc.), and parentheses.
 *
 * The variable (default "x") resolves to the function argument at call time.
 * Constants: PI, pi → Math.PI, E → Math.E.
 *
 * @example
 * const f = compileExpression("x * x - 4");
 * f(3); // 5
 *
 * const g = compileExpression("3 * cos(t)", "t");
 * g(Math.PI); // -3
 */
export function compileExpression(expression: string, variableName = 'x'): (value: number) => number {
  let currentValue = 0;

  const resolver: ValueResolver = {
    resolveNumber: (name: string): number => {
      if (name === variableName) {
        return currentValue;
      }
      if (name === 'PI' || name === 'pi') {
        return Math.PI;
      }
      if (name === 'e') {
        return Math.E;
      }
      return 0;
    },
  };

  const thunk = parseExpressionThunk(expression, resolver);

  return (value: number): number => {
    currentValue = value;
    return thunk();
  };
}
